import * as React from 'react';
import { Box, Grid } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { object, string } from 'yup';
import dayjs from 'dayjs';
import {
  type ProfessionalQualificationFormValues,
  type FormikProp,
} from './types';
import { PAGE_TEXT } from '../lang';
import {
  professionalFieldOptions,
  professionalQualificationOptions,
} from './options';
import {
  Button,
  TextField,
  Select,
  DatePicker,
  type SelectOption,
} from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';

export type ProfessionalQualificationFormProps = {
  initialValues: ProfessionalQualificationFormValues;
  enableEdit: boolean;
  enableDelete: boolean;
  countryList: SelectOption[];
  professionalQualificationIndex: number;
  onSubmit: (values: ProfessionalQualificationFormValues) => void;
  onDelete: (e: React.MouseEvent) => void;
};

export const ProfessionalQualificationForm: React.FunctionComponent<
  ProfessionalQualificationFormProps
> = props => {
  const {
    initialValues,
    enableEdit,
    professionalQualificationIndex,
    countryList: nationalityOptions,
    onSubmit,
    onDelete,
    enableDelete,
  } = props;
  const {
    OTHER_INFORMATION: { PROFESSIONAL_QUALIFICATION },
  } = PAGE_TEXT;

  const formik: FormikProp = useFormik({
    initialValues,
    validationSchema: object({
      awardDate: string().required('Required'),
      awardName: string()
        .required('Required')
        .test(
          'byte-length',
          'Exceeds maximum length of 100 characters',
          value => {
            return value?.length <= 100;
          },
        ),
      country: string().required('Required'),
      professionField: string().required('Required'),
      professionalQualification: string().required('Required'),
      awardBodyName: string()
        .required('Required')
        .test(
          'byte-length',
          'Exceeds maximum length of 100 characters',
          value => {
            return value?.length <= 100;
          },
        ),
      attainmentMeans: string()
        .required('Required')
        .test(
          'byte-length',
          'Exceeds maximum length of 50 characters',
          value => {
            return value?.length <= 50;
          },
        ),
    }),
    onSubmit,
  });

  return (
    <FormikProvider value={formik}>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        onSubmit={handleReadySubmit(formik, professionalQualificationIndex)}
        // prevent browser validate, otherwise will block formik setTouched when submitting
        noValidate
      >
        <Grid container columnSpacing={4} rowSpacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <TextField
              className="w-full"
              disabled={!enableEdit}
              label={PROFESSIONAL_QUALIFICATION.AWARD_NAME}
              id="professional-qualification-award-name"
              name="awardName"
              required
              InputLabelProps={{ shrink: true }}
              value={formik.values.awardName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.awardName && Boolean(formik.errors.awardName)
              }
              helperText={formik.touched.awardName && formik.errors.awardName}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <DatePicker
              className="w-full"
              disabled={!enableEdit}
              label={PROFESSIONAL_QUALIFICATION.AWARD_DATE}
              id="professional-qualification-award-date"
              name="awardDate"
              required
              InputLabelProps={{ shrink: true }}
              maxDate={dayjs()}
              value={
                formik.values.awardDate !== null
                  ? dayjs(formik.values.awardDate)
                  : null
              }
              onChange={date => {
                void formik.setFieldValue('awardDate', date);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.awardDate && Boolean(formik.errors.awardDate)
              }
              helperText={formik.touched.awardDate && formik.errors.awardDate}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Select
              className="w-full"
              disabled={!enableEdit}
              options={nationalityOptions}
              label={PROFESSIONAL_QUALIFICATION.COUNTRY}
              id="professional-qualification-country"
              name="country"
              required
              InputLabelProps={{ shrink: true }}
              value={formik.values.country ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.country && Boolean(formik.errors.country)}
              helperText={formik.touched.country && formik.errors.country}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Select
              className="w-full"
              disabled={!enableEdit}
              options={professionalFieldOptions}
              label={PROFESSIONAL_QUALIFICATION.FIELD_OF_PROFESSION}
              id="professional-qualification-profession-field"
              name="professionField"
              required
              InputLabelProps={{ shrink: true }}
              value={formik.values.professionField}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.professionField &&
                Boolean(formik.errors.professionField)
              }
              helperText={
                formik.touched.professionField && formik.errors.professionField
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Select
              className="w-full"
              disabled={!enableEdit}
              options={professionalQualificationOptions}
              label={PROFESSIONAL_QUALIFICATION.PROFESSIONAL_QUALIFICATION}
              id="professional-qualification-professional-qualification"
              name="professionalQualification"
              required
              InputLabelProps={{ shrink: true }}
              value={formik.values.professionalQualification}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.professionalQualification &&
                Boolean(formik.errors.professionalQualification)
              }
              helperText={
                formik.touched.professionalQualification &&
                formik.errors.professionalQualification
              }
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <TextField
              className="w-full"
              disabled={!enableEdit}
              label={PROFESSIONAL_QUALIFICATION.NAME_OF_AWARD_BODY}
              id="professional-qualification-award-body-name"
              name="awardBodyName"
              required
              InputLabelProps={{ shrink: true }}
              value={formik.values.awardBodyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.awardBodyName &&
                Boolean(formik.errors.awardBodyName)
              }
              helperText={
                formik.touched.awardBodyName && formik.errors.awardBodyName
              }
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <TextField
              className="w-full"
              disabled={!enableEdit}
              label={PROFESSIONAL_QUALIFICATION.MEANS_OF_ATTAINMENT}
              id="language-qualification-attainment-means"
              name="attainmentMeans"
              required
              InputLabelProps={{ shrink: true }}
              value={formik.values.attainmentMeans}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g. Examination, Competition, Nomination, Selection"
              error={
                formik.touched.attainmentMeans &&
                Boolean(formik.errors.attainmentMeans)
              }
              helperText={
                formik.touched.attainmentMeans && formik.errors.attainmentMeans
              }
            />
          </Grid>
        </Grid>
        {enableEdit && (
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              style={{ marginRight: '10px' }}
            >
              {PAGE_TEXT.BUTTON.SAVE}
            </Button>
            {enableDelete && (
              <Button variant="outlined" onClick={onDelete}>
                {PAGE_TEXT.BUTTON.DELETE}
              </Button>
            )}
          </Box>
        )}
      </form>
    </FormikProvider>
  );
};
